import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

interface HilfeCategory {
  id: string;
  label: string;
  icon: string;
}
export interface VerzeichnisType {
  id: string;
  label: string;
}
interface AppConfig {
  vereinTags: string[];
  companyTags: string[];
  vereinTypes: VerzeichnisType[];
  companyTypes: VerzeichnisType[];
  hilfeCategories: HilfeCategory[];
  marketCategories: string[];
  marketConditions: string[];
  marketGenders: string[];
  marketSizesByCategory: MarketSizesByCategory;
  contactChannels: string[];
}

export type MarketSizesByCategory = Record<string, Record<string, string[]>>;

const FALLBACK_VEREIN_TAGS = [
  'Sport',
  'Kultur',
  'Soziales',
  'Jugend',
  'Senioren',
  'Natur',
  'Musik',
  'Sicherheit',
  'Religion',
  'Gesellschaft',
];
const FALLBACK_COMPANY_TAGS = [
  'Handwerk',
  'Gastronomie',
  'Einkaufen',
  'Dienstleistung',
  'Gesundheit',
  'Transport',
  'Haushalt',
  'Technik',
  'Industrie',
  'Kultur',
];
const FALLBACK_COMPANY_TYPES: VerzeichnisType[] = [
  { id: 'dienstleistung', label: 'Dienstleistung' },
  { id: 'einkaufen', label: 'Einkaufen' },
  { id: 'gastronomie', label: 'Gastronomie' },
  { id: 'gesundheit', label: 'Gesundheit' },
  { id: 'handwerk', label: 'Handwerk' },
  { id: 'industrie', label: 'Industrie' },
  { id: 'kultur', label: 'Kultur' },
  { id: 'selbststaendig', label: 'Selbständig' },
  { id: 'sonstiges', label: 'Sonstiges' },
];
const FALLBACK_VEREIN_TYPES: VerzeichnisType[] = [
  { id: 'gesellschaft', label: 'Gesellschaft' },
  { id: 'jugend', label: 'Jugend' },
  { id: 'kultur', label: 'Kultur' },
  { id: 'natur', label: 'Natur' },
  { id: 'sicherheit', label: 'Sicherheit' },
  { id: 'sonstiges', label: 'Sonstiges' },
  { id: 'soziales', label: 'Soziales' },
  { id: 'sport', label: 'Sport' },
];
export const FALLBACK_MARKET_CATEGORIES = [
  'Basteln & Werken',
  'Bücher & Medien',
  'Büro & Schule',
  'Elektronik',
  'Garten & Pflanzen',
  'Handarbeit & Selbergemacht',
  'Haushalt & Küche',
  'Kinder & Baby',
  'Kleidung',
  'Möbel & Wohnen',
  'Schuhe',
  'Sport & Freizeit',
  'Tiere & Zubehör',
  'Werkzeug & Technik',
  'Sonstiges',
];
const FALLBACK_MARKET_CONDITIONS = ['Neu', 'Sehr gut', 'Gut', 'Akzeptabel'];
const FALLBACK_MARKET_GENDERS = ['damen', 'herren', 'kinder'];
const MARKET_CATEGORY_SHOES = 'Schuhe';
const CLOTHING_SIZES_KINDER = [
  '50–74',
  '80–86',
  '92–98',
  '104–110',
  '116–122',
  '128–134',
  '140–146',
  '152–164',
];
const CLOTHING_SIZES_ADULT = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL+'];
const euShoeSizes = (from: number, to: number): string[] =>
  Array.from({ length: to - from + 1 }, (_, index) => String(from + index));
const FALLBACK_MARKET_SIZES_BY_CATEGORY: MarketSizesByCategory = {
  Kleidung: {
    damen: CLOTHING_SIZES_ADULT,
    herren: CLOTHING_SIZES_ADULT,
    kinder: CLOTHING_SIZES_KINDER,
  },
  [MARKET_CATEGORY_SHOES]: {
    damen: euShoeSizes(35, 43),
    herren: euShoeSizes(38, 48),
    kinder: euShoeSizes(18, 35),
  },
};
const FALLBACK_CONTACT_CHANNELS = ['CALL', 'WHATSAPP', 'SIGNAL', 'TELEGRAM', 'THREEMA'];
export const FALLBACK_HILFE_CATEGORIES: HilfeCategory[] = [
  { id: 'Beratung', label: 'Beratung', icon: 'chatbubble-ellipses-outline' },
  { id: 'Einkauf', label: 'Einkauf', icon: 'cart-outline' },
  { id: 'Handwerk', label: 'Handwerk', icon: 'hammer-outline' },
  { id: 'Haushalt & Kochen', label: 'Haushalt & Kochen', icon: 'home-outline' },
  { id: 'Kinderbetreuung', label: 'Kinderbetreuung', icon: 'happy-outline' },
  { id: 'Pflanzen & Tiere', label: 'Pflanzen & Tiere', icon: 'leaf-outline' },
  { id: 'Schule', label: 'Schule', icon: 'school-outline' },
  { id: 'Transport', label: 'Transport', icon: 'car-outline' },
  { id: 'Sonstiges', label: 'Sonstiges', icon: 'ellipsis-horizontal-outline' },
];

@Injectable({ providedIn: 'root' })
export class ConfigService {
  private http = inject(HttpClient);

  readonly vereinTags = signal<string[]>(FALLBACK_VEREIN_TAGS);
  readonly companyTags = signal<string[]>(FALLBACK_COMPANY_TAGS);
  readonly vereinTypes = signal<VerzeichnisType[]>(FALLBACK_VEREIN_TYPES);
  readonly companyTypes = signal<VerzeichnisType[]>(FALLBACK_COMPANY_TYPES);
  readonly hilfeCategories = signal<HilfeCategory[]>(FALLBACK_HILFE_CATEGORIES);
  readonly marketCategories = signal<string[]>(FALLBACK_MARKET_CATEGORIES);
  readonly marketConditions = signal<string[]>(FALLBACK_MARKET_CONDITIONS);
  readonly marketGenders = signal<string[]>(FALLBACK_MARKET_GENDERS);
  readonly marketSizesByCategory = signal<MarketSizesByCategory>(FALLBACK_MARKET_SIZES_BY_CATEGORY);
  readonly contactChannels = signal<string[]>(FALLBACK_CONTACT_CHANNELS);

  load(): void {
    this.http.get<AppConfig>(`${environment.apiUrl}/api/config`).subscribe({
      next: (cfg) => {
        if (cfg.vereinTags?.length) this.vereinTags.set(cfg.vereinTags);
        if (cfg.companyTags?.length) this.companyTags.set(cfg.companyTags);
        if (cfg.vereinTypes?.length) this.vereinTypes.set(cfg.vereinTypes);
        if (cfg.companyTypes?.length) this.companyTypes.set(cfg.companyTypes);
        if (cfg.hilfeCategories?.length) this.hilfeCategories.set(cfg.hilfeCategories);
        if (cfg.marketCategories?.length) this.marketCategories.set(cfg.marketCategories);
        if (cfg.marketConditions?.length) this.marketConditions.set(cfg.marketConditions);
        if (cfg.marketGenders?.length) this.marketGenders.set(cfg.marketGenders);
        if (cfg.marketSizesByCategory && Object.keys(cfg.marketSizesByCategory).length) {
          this.marketSizesByCategory.set(cfg.marketSizesByCategory);
        }
        if (cfg.contactChannels?.length) this.contactChannels.set(cfg.contactChannels);
      },
    });
  }

  isSizedMarketCategory(category: string | null | undefined): boolean {
    return !!category && category in this.marketSizesByCategory();
  }

  marketSizesFor(category: string | null | undefined, gender: string | null | undefined): string[] {
    if (!category || !gender) return [];
    return this.marketSizesByCategory()[category]?.[gender] ?? [];
  }

  marketSizeLabelKey(category: string | null | undefined): string {
    return category === MARKET_CATEGORY_SHOES
      ? 'marktplatz.form_fields.shoe_size'
      : 'marktplatz.form_fields.size';
  }
}
